import { apiService } from './api';
import { getApiConfig } from '../config/api';

// Get API configuration
const apiConfig = getApiConfig();

// Share Link Response from Backend
export interface ShareLinkDto {
  id: number;
  shareLinkName: string;
  devices: number; // Count of devices
  userId: number;
  url: string;
  createdOn: string;
  details: ShareLinkDetailResponseDto[];
}

export interface ShareLinkDetailResponseDto {
  id: number;
  deviceId: number;
  deviceName: string;
  parameterIds: string; // Comma-separated string
  createdOn: string;
}

// Create Share Link Request
export interface CreateShareLinkRequest {
  shareLinkName: string;
  url: string;
  details: ShareLinkDetailDto[];
}

export interface ShareLinkDetailDto {
  deviceId: number;
  parameterIds: string; // Comma-separated parameter IDs (e.g., "1,2,3,4")
}

// Update Share Link Request (if needed in future)
export interface UpdateShareLinkRequest {
  shareLinkName: string;
  url: string;
  details: ShareLinkDetailDto[];
}

export const shareLinkApi = {
  // Get all share links for the current user
  getShareLinks: async (): Promise<ShareLinkDto[]> => {
    const response = await apiService.request<ShareLinkDto[]>('/Secure/sharelinks');
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch share links');
    }
    return response.data;
  },

  // Create a new share link
  createShareLink: async (data: CreateShareLinkRequest): Promise<ShareLinkDto> => {
    const response = await apiService.request<ShareLinkDto>('/Secure/sharelinks', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to create share link');
    }
    return response.data;
  },

  // Update an existing share link (if endpoint exists)
  updateShareLink: async (id: number, data: UpdateShareLinkRequest): Promise<ShareLinkDto> => {
    const response = await apiService.request<ShareLinkDto>(`/Secure/sharelinks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to update share link');
    }
    return response.data;
  },

  // Delete a share link
  deleteShareLink: async (id: number): Promise<boolean> => {
    const response = await apiService.request<{ message: string }>(`/Secure/sharelinks/${id}`, {
      method: 'DELETE',
    });
    return !!response.success;
  },

  // Get share link by ID
  getShareLinkById: async (id: number): Promise<ShareLinkDto> => {
    const response = await apiService.request<ShareLinkDto>(`/Secure/sharelinks/${id}`);
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch share link');
    }
    return response.data;
  },

  // Get public share link data with live device readings (no authentication required)
  getPublicShareLinkData: async (url: string): Promise<PublicShareLinkData> => {
    const response = await fetch(`${apiConfig.BASE_URL}/public/PublicShare/${url}`);
    if (!response.ok) {
      throw new Error('Failed to fetch public share link data');
    }
    return response.json();
  },
};

// Public share link interfaces
export interface PublicShareLinkData {
  id: number;
  shareLinkName: string;
  url: string;
  createdOn: string;
  AQIStandard: number; // 1 = US, 2 = UAE, 3 = India
  devices: PublicDeviceLiveData[];
}

export interface PublicDeviceLiveData {
  deviceId: number;
  deviceName: string;
  deviceLocationType: string;
  serialNumber?: string;
  installationLocation?: string;
  deviceImageUrl?: string;
  lastUpdated: string;
  parameters: LiveParameterData[];
}

export interface LiveParameterData {
  parameterDriverId: number;
  parameterName: string;
  parameterDisplayName: string;
  parameterUnit: string;
  parameterValue?: number;
  lastRecordedAt?: string;
  subIndexUS?: number;
  subIndexUAE?: number;
  subIndexIN?: number;
  preferredUnitName?: string;
}

export default shareLinkApi;
